import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Avatar,
  Stack,
  Divider,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectItem,
  Tooltip,
  useMediaQuery,
  useTheme,
  Autocomplete,
  Snackbar,
  Alert,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Paper,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  People as PeopleIcon,
  Security as SecurityIcon,
  VpnKey as VpnKeyIcon,
  Public as PublicIcon,
  Notifications as NotificationsIcon,
  Add as AddIcon,
  MoreHoriz as MoreHorizIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import apiEndpoints from "../../apiconfig";
import { useNavigate } from "react-router-dom";

const roleChipStyles = (role) => {
  switch (role) {
    case "Super Admin":
      return { bgcolor: "#fee2e2", color: "#991b1b", borderColor: "#fecaca" };
    case "Admin":
      return { bgcolor: "#e0f2fe", color: "#0369a1", borderColor: "#bae6fd" };
    case "Moderator":
      return { bgcolor: "#f3e8ff", color: "#6b21a8", borderColor: "#e9d5ff" };
    case "Support":
      return { bgcolor: "#fef9c3", color: "#854d0e", borderColor: "#fde047" };
    default:
      return { bgcolor: "#f3f4f6", color: "#374151", borderColor: "#e5e7eb" };
  }
};

const statusChipStyles = (status) =>
  status === "active"
    ? { bgcolor: "#dcfce7", color: "#166534", borderColor: "#bbf7d0" }
    : { bgcolor: "#f3f4f6", color: "#374151", borderColor: "#e5e7eb" };

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

// -------------------- API Service Functions --------------------
const getAuthToken = () => {
  return localStorage.getItem("authToken");
};

// Function to extract admin_guid from JWT token
const getAdminGuidFromToken = () => {
  try {
    const token = getAuthToken();
    if (!token) return null;

    // Extract payload from JWT token
    const payload = token.split(".")[1];
    const decodedPayload = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const payloadData = JSON.parse(decodedPayload);

    return payloadData.userGuid || null;
  } catch (error) {
    console.error("Error extracting admin_guid from token:", error);
    return null;
  }
};

const fetchProfileData = async () => {
  try {
    const authToken = getAuthToken();
    if (!authToken) {
      throw new Error("No authentication token found");
    }

    const admin_guid = getAdminGuidFromToken();
    if (!admin_guid) {
      throw new Error("Could not extract admin_guid from token");
    }

    const response = await fetch(apiEndpoints.adminCrud, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "getProfile",
        admin_guid: admin_guid,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching profile data:", error);
    throw error;
  }
};

const updateProfileData = async (profileData) => {
  try {
    const authToken = getAuthToken();
    if (!authToken) {
      throw new Error("No authentication token found");
    }

    const admin_guid = getAdminGuidFromToken();
    if (!admin_guid) {
      throw new Error("Could not extract admin_guid from token");
    }

    const payload = {
      action: "updateProfile",
      admin_guid: admin_guid,
      username: profileData.name,
      email: profileData.email,
      phone: profileData.phone,
    };

    const response = await fetch(apiEndpoints.adminCrud, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating profile data:", error);
    throw error;
  }
};

const changePassword = async (oldPassword, newPassword) => {
  try {
    const authToken = getAuthToken();
    if (!authToken) {
      throw new Error("No authentication token found");
    }

    const admin_guid = getAdminGuidFromToken();
    if (!admin_guid) {
      throw new Error("Could not extract admin_guid from token");
    }

    const payload = {
      action: "changePassword",
      admin_guid: admin_guid,
      oldPassword: oldPassword,
      newPassword: newPassword,
    };

    const response = await fetch(apiEndpoints.adminCrud, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};

// -------------------- PAGE --------------------
export default function AdminSettings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [tab, setTab] = useState("profile-settings");
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const navigate = useNavigate();
  const [editPermissionsMode, setEditPermissionsMode] = useState(false);
  const handleSavePermissions = async () => {
    try {
      for (const user of users) {
        const permissionsArray = availablePages.filter(
          (p) => user.permissionsJSON[p] === 1,
        );
        const authToken = getAuthToken();
        await fetch(`${apiEndpoints.permission}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: "updatePermissions",
            admin_guid: user.admin_guid,
            permissions: user.permissionsJSON,
          }),
        });
      }

      setSnackbar({
        open: true,
        message: "Permissions updated successfully!",
        severity: "success",
      });

      setEditPermissionsMode(false);
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to update permissions",
        severity: "error",
      });
    }
  };

  const handleAddUserSave = async () => {
    if (
      !newUser.name ||
      !newUser.email ||
      !newUser.phone ||
      !newUser.password
    ) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields",
        severity: "error",
      });
      return;
    }

    try {
      const authToken = getAuthToken();
      if (!authToken) throw new Error("No auth token found");

      // Convert boolean permissions -> 1 or 0
      const normalizedPermissions = Object.fromEntries(
        Object.entries(newUser.permissions).map(([key, value]) => [
          key,
          value ? 1 : 0,
        ]),
      );

      const payload = {
        action: "addUser",
        username: newUser.name,
        email: newUser.email,
        phone: newUser.phone,
        password: newUser.password,
        role: newUser.role,
        permissions: normalizedPermissions, // <- now integers
      };

      const response = await fetch(apiEndpoints.permission, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        setSnackbar({
          open: true,
          message: "User added successfully!",
          severity: "success",
        });

        setAddUserOpen(false);
        setNewUser({
          name: "",
          email: "",
          phone: "",
          password: "",
          role: "Staff",
          permissions: availablePages.reduce(
            (acc, page) => ({ ...acc, [page]: 0 }), // default 0 now
            {},
          ),
        });

        fetchUsers(); // reload table
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to add user",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error adding user:", error);
      setSnackbar({
        open: true,
        message: "Error adding user",
        severity: "error",
      });
    }
  };

  // Edit mode states
  const [editProfileMode, setEditProfileMode] = useState(false);
  const [editPasswordMode, setEditPasswordMode] = useState(false);

  // Password visibility state
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const availablePages = [
    "dashboard",
    "user",
    "enquiries",
    "approvals",
    "payments",
    "reports",
    "settings",
  ];

  const [users, setUsers] = useState([]); // empty initially

  const fetchUsers = async () => {
    try {
      const authToken = getAuthToken(); // same as your JWT logic
      const response = await fetch(`${apiEndpoints.permission}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "fetchAllUsers" }),
      });

      const data = await response.json();
      if (data.success) {
        // Transform permissions to match your checkbox format
        const transformedUsers = data.data.map((user) => {
          const permissionsJSON = {};
          availablePages.forEach((page) => {
            // backend sends object {dashboard:1, user:1, ...}
            permissionsJSON[page] = user.permissions[page] === 1 ? 1 : 0;
          });

          return {
            ...user,
            name: user.username,
            role: user.role || "User",
            permissionsJSON,
          };
        });

        setUsers(transformedUsers);
      } else {
        console.error("Failed to fetch users:", data.message);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [systemSettings, setSystemSettings] = useState({});

  // Profile settings state
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Original profile data to revert changes if needed
  const [originalProfileData, setOriginalProfileData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "Staff",
    permissions: availablePages.reduce(
      (acc, page) => ({ ...acc, [page]: false }),
      {},
    ),
  });

  // Fetch profile data on component mount
  useEffect(() => {
    const loadProfileData = async () => {
      setLoading(true);
      try {
        const data = await fetchProfileData();
        if (data.success && data.data) {
          setProfileData((prev) => ({
            ...prev,
            name: data.data.username || "",
            email: data.data.email || "",
            phone: data.data.phone || "",
          }));
          setOriginalProfileData({
            name: data.data.username || "",
            email: data.data.email || "",
            phone: data.data.phone || "",
          });
        } else {
          setSnackbar({
            open: true,
            message: data.message || "Failed to load profile data",
            severity: "error",
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Failed to load profile data",
          severity: "error",
        });
        console.error("Error loading profile data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadProfileData();
  }, []);

  const handlePermissionChange = async (userId, page, checked) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;

    // update local state
    const newPermissions = { ...user.permissionsJSON, [page]: checked ? 1 : 0 };
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId ? { ...u, permissionsJSON: newPermissions } : u,
      ),
    );

    // ✅ convert to array before sending
    const permissionsArray = availablePages.filter(
      (p) => newPermissions[p] === 1,
    );

    try {
      const authToken = getAuthToken();
      const response = await fetch(`${apiEndpoints.permission}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "updatePermissions",
          admin_guid: user.admin_guid,
          permissions: newPermissions,
        }),
      });

      const data = await response.json();
      if (!data.success) {
        console.error("Failed to update permissions:", data.message);
      }
    } catch (error) {
      console.error("Error updating permissions:", error);
    }
  };

  // -------------------- Password Visibility Handlers --------------------
  const handleClickShowPassword = (field) => {
    setShowPassword({
      ...showPassword,
      [field]: !showPassword[field],
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // -------------------- Profile Settings Handlers --------------------
  const handleProfileChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditProfile = () => {
    setEditProfileMode(true);
  };

  const handleCancelProfileEdit = () => {
    setProfileData((prev) => ({
      ...prev,
      name: originalProfileData.name,
      email: originalProfileData.email,
      phone: originalProfileData.phone,
    }));
    setEditProfileMode(false);
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const result = await updateProfileData(profileData);
      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message || "Profile updated successfully!",
          severity: "success",
        });
        setOriginalProfileData({
          name: profileData.name,
          email: profileData.email,
          phone: profileData.phone,
        });
        setEditProfileMode(false);
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to update profile",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to update profile",
        severity: "error",
      });
      console.error("Error updating profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPassword = () => {
    setEditPasswordMode(true);
  };

  const handleCancelPasswordEdit = () => {
    setProfileData((prev) => ({
      ...prev,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    }));
    setEditPasswordMode(false);
  };

  const handleChangePassword = async () => {
    // Validate passwords
    if (!profileData.currentPassword || !profileData.newPassword) {
      setSnackbar({
        open: true,
        message: "Please fill in all password fields",
        severity: "error",
      });
      return;
    }

    if (profileData.newPassword !== profileData.confirmPassword) {
      setSnackbar({
        open: true,
        message: "New passwords do not match!",
        severity: "error",
      });
      return;
    }

    setPasswordLoading(true);
    try {
      const result = await changePassword(
        profileData.currentPassword,
        profileData.newPassword,
      );
      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message || "Password changed successfully!",
          severity: "success",
        });

        // Reset password fields
        setProfileData((prev) => ({
          ...prev,
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }));
        setEditPasswordMode(false);
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to change password",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to change password",
        severity: "error",
      });
      console.error("Error changing password:", error);
    } finally {
      setPasswordLoading(false);
    }
  };

  // -------------------- Roles: dialogs + actions --------------------
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [newRole, setNewRole] = useState({
    name: "",
    description: "",
    permissions: [],
  });

  // Permission matrix helper: roles that have a permission
  const rolesByPermission = useMemo(() => {
    const map = {};
    permissions.forEach((p) => {
      map[p.id] = roles
        .filter(
          (r) => r.permissions.includes("all") || r.permissions.includes(p.id),
        )
        .map((r) => r.name);
    });
    return map;
  }, [roles, permissions]);

  // -------------------- System Settings Handlers --------------------
  const setSystem = (key, value) =>
    setSystemSettings((prev) => ({ ...prev, [key]: value }));

  const saveAll = () => {
    // mock save
    console.log("Saving settings:", {
      systemSettings,
      profileData,
    });
    setSnackbar({
      open: true,
      message: "All settings saved successfully!",
      severity: "success",
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // -------------------- UI --------------------
  return (
    <Box sx={{ width: "100%", maxWidth: "1600px", mx: "auto", pb: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          AdminSettings
        </Typography>
        <Typography color="text.secondary" variant="body1">
          Manage site configuration, your profile, and system users.
        </Typography>
      </Box>
      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          borderRadius: 2,
          overflow: "visible",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={false}
          textColor="primary"
          indicatorColor="secondary"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 48,
              fontSize: "0.95rem",
            },
          }}
        >
          <Tab
            value="profile-settings"
            label="Profile Settings"
            icon={<PersonIcon />}
            iconPosition="start"
          />
          <Tab
            value="roles-permissions"
            label="Roles & Permissions"
            icon={<SecurityIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>
      {/* -------------------- Profile Settings Tab -------------------- */}
      {tab === "profile-settings" && (
        <Box mt={3} width={"100%"}>
          <Grid
            container
            spacing={3}
            width={"100%"}
            justifyContent={"space-between"}
          >
            {/* Personal Information */}
            <Grid item width={{ xs: "100%", sm: "48%" }}>
              <Card sx={{ height: "100%", overflow: "visible" }}>
                <CardHeader
                  title={
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar
                          sx={{
                            bgcolor: "primary.main",
                            width: 32,
                            height: 32,
                          }}
                        >
                          <PersonIcon
                            sx={{ fontSize: 20, color: "primary.contrastText" }}
                          />
                        </Avatar>
                        <Stack>
                          <Typography variant="h6" fontWeight={700}>
                            Personal Information
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Your basic profile details
                          </Typography>
                        </Stack>
                      </Stack>
                      {!editProfileMode && (
                        <Button
                          startIcon={<EditIcon />}
                          onClick={handleEditProfile}
                          size="small"
                          color="secondary"
                          sx={{ minWidth: "auto" }}
                        >
                          Edit
                        </Button>
                      )}
                    </Stack>
                  }
                  sx={{ pb: 0 }}
                />
                <Divider sx={{ my: 2, mx: 2 }} />
                <CardContent sx={{ pt: 1 }}>
                  <Stack spacing={3}>
                    <TextField
                      label="Full Name"
                      value={profileData.name}
                      onChange={(e) =>
                        handleProfileChange("name", e.target.value)
                      }
                      fullWidth
                      disabled={!editProfileMode || loading}
                      variant={editProfileMode ? "outlined" : "filled"}
                    />
                    <TextField
                      label="Email Address"
                      type="email"
                      value={profileData.email}
                      onChange={(e) =>
                        handleProfileChange("email", e.target.value)
                      }
                      fullWidth
                      disabled={!editProfileMode || loading}
                      variant={editProfileMode ? "outlined" : "filled"}
                    />
                    <TextField
                      label="Phone Number"
                      value={profileData.phone}
                      onChange={(e) =>
                        handleProfileChange("phone", e.target.value)
                      }
                      fullWidth
                      disabled={!editProfileMode || loading}
                      variant={editProfileMode ? "outlined" : "filled"}
                    />
                    {editProfileMode && (
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        spacing={2}
                        pt={1}
                      >
                        <Button
                          variant="outlined"
                          color="inherit"
                          onClick={handleCancelProfileEdit}
                          disabled={loading}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={handleSaveProfile}
                          disabled={loading}
                          startIcon={<SaveIcon />}
                        >
                          {loading ? "Saving..." : "Save Changes"}
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Change Password */}
            <Grid item width={{ xs: "100%", sm: "48%" }}>
              <Card sx={{ height: "100%" }}>
                <CardHeader
                  title={
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar
                          sx={{
                            bgcolor: "warning.light",
                            color: "warning.dark",
                            width: 32,
                            height: 32,
                          }}
                        >
                          <VpnKeyIcon sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Stack>
                          <Typography variant="h6" fontWeight={700}>
                            Change Password
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Ensure a strong password
                          </Typography>
                        </Stack>
                      </Stack>
                      {!editPasswordMode && (
                        <Button
                          startIcon={<EditIcon />}
                          onClick={handleEditPassword}
                          size="small"
                          color="secondary"
                        >
                          Change
                        </Button>
                      )}
                    </Stack>
                  }
                  sx={{ pb: 0 }}
                />
                <Divider sx={{ my: 2, mx: 2 }} />
                <CardContent sx={{ pt: 1 }}>
                  <Stack spacing={3}>
                    <TextField
                      label="Current Password"
                      type={showPassword.currentPassword ? "text" : "password"}
                      value={profileData.currentPassword}
                      onChange={(e) =>
                        handleProfileChange("currentPassword", e.target.value)
                      }
                      fullWidth
                      disabled={!editPasswordMode || passwordLoading}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                handleClickShowPassword("currentPassword")
                              }
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showPassword.currentPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      label="New Password"
                      type={showPassword.newPassword ? "text" : "password"}
                      value={profileData.newPassword}
                      onChange={(e) =>
                        handleProfileChange("newPassword", e.target.value)
                      }
                      fullWidth
                      disabled={!editPasswordMode || passwordLoading}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                handleClickShowPassword("newPassword")
                              }
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showPassword.newPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      label="Confirm New Password"
                      type={showPassword.confirmPassword ? "text" : "password"}
                      value={profileData.confirmPassword}
                      onChange={(e) =>
                        handleProfileChange("confirmPassword", e.target.value)
                      }
                      fullWidth
                      disabled={!editPasswordMode || passwordLoading}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                handleClickShowPassword("confirmPassword")
                              }
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showPassword.confirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {editPasswordMode && (
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        spacing={2}
                        pt={1}
                      >
                        <Button
                          variant="outlined"
                          color="inherit"
                          onClick={handleCancelPasswordEdit}
                          disabled={passwordLoading}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={handleChangePassword}
                          disabled={passwordLoading}
                        >
                          {passwordLoading ? "Updating..." : "Update Password"}
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}{" "}
      {/* -------------------- Roles & Permissions Tab -------------------- */}
      {tab === "roles-permissions" && (
        <Box mt={3} width="100%">
          <Card sx={{ overflow: "hidden" }}>
            <Box
              sx={{
                p: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  User Roles & Permissions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage who can access what
                </Typography>
              </Box>
              <Stack direction="row" spacing={2}>
                <Button
                  variant={editPermissionsMode ? "contained" : "outlined"}
                  color={editPermissionsMode ? "primary" : "inherit"}
                  startIcon={editPermissionsMode ? <SaveIcon /> : <EditIcon />}
                  onClick={
                    editPermissionsMode
                      ? handleSavePermissions
                      : () => setEditPermissionsMode(true)
                  }
                >
                  {editPermissionsMode ? "Save Changes" : "Edit Permissions"}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={() => setAddUserOpen(true)}
                >
                  Add User
                </Button>
              </Stack>
            </Box>
            <Divider />
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <DataGrid
                autoHeight
                rows={users}
                getRowId={(row) => row.id}
                columns={[
                  {
                    field: "name",
                    headerName: "User",
                    flex: 1.5,
                    minWidth: 200,
                    renderCell: (params) => (
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        height="100%"
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: "primary.light",
                            fontSize: "0.875rem",
                          }}
                        >
                          {params.value?.charAt(0).toUpperCase()}
                        </Avatar>
                        <Stack>
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            lineHeight={1.2}
                          >
                            {params.value}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {params.row.username}
                          </Typography>
                        </Stack>
                      </Stack>
                    ),
                  },
                  {
                    field: "email",
                    headerName: "Email",
                    flex: 1.5,
                    minWidth: 220,
                  },
                  {
                    field: "role",
                    headerName: "Role",
                    flex: 1,
                    minWidth: 140,
                    renderCell: (params) => (
                      <Chip
                        label={params.value}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          borderRadius: "6px",
                          ...roleChipStyles(params.value),
                        }}
                      />
                    ),
                  },
                  ...availablePages.map((page) => ({
                    field: page,
                    headerName: page.charAt(0).toUpperCase() + page.slice(1),
                    width: 100,
                    renderCell: (params) => {
                      const user = params.row;
                      const checked = user.permissionsJSON[page] === 1;
                      return (
                        <Checkbox
                          checked={checked}
                          disabled={!editPermissionsMode}
                          color="secondary"
                          size="small"
                          onChange={(e) =>
                            handlePermissionChange(
                              user.id,
                              page,
                              e.target.checked,
                            )
                          }
                        />
                      );
                    },
                    sortable: false,
                    filterable: false,
                    align: "center",
                    headerAlign: "center",
                  })),
                ]}
                pageSize={10}
                rowsPerPageOptions={[5, 10, 25]}
                checkboxSelection={false}
                disableSelectionOnClick
                sx={{
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": {
                    bgcolor: "background.default",
                    color: "text.secondary",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  },
                  "& .MuiDataGrid-cell": {
                    borderColor: "divider",
                  },
                  "& .MuiDataGrid-row:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              />
            </Box>
          </Card>
        </Box>
      )}
      {/* Add User Dialog */}
      <Dialog
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {/* Name */}
            <TextField
              label="Full Name"
              value={newUser.name}
              onChange={(e) =>
                setNewUser((p) => ({ ...p, name: e.target.value }))
              }
              fullWidth
              size={isMobile ? "small" : "medium"}
            />
            {/* Email */}
            <TextField
              label="Email"
              type="email"
              value={newUser.email}
              onChange={(e) =>
                setNewUser((p) => ({ ...p, email: e.target.value }))
              }
              fullWidth
              size={isMobile ? "small" : "medium"}
            />
            {/* Phone */}
            <TextField
              label="Phone Number"
              value={newUser.phone}
              onChange={(e) =>
                setNewUser((p) => ({ ...p, phone: e.target.value }))
              }
              fullWidth
              size={isMobile ? "small" : "medium"}
            />
            {/* Password */}
            <TextField
              label="Password"
              type="password"
              value={newUser.password}
              onChange={(e) =>
                setNewUser((p) => ({ ...p, password: e.target.value }))
              }
              fullWidth
              size={isMobile ? "small" : "medium"}
            />
            {/* Role Select */}
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>Role</InputLabel>
              <Select
                value={newUser.role}
                onChange={(e) =>
                  setNewUser((p) => ({ ...p, role: e.target.value }))
                }
                label="Role"
              >
                <SelectItem value="Admin">Admin</SelectItem>
                <SelectItem value="Staff">Staff</SelectItem>
              </Select>
            </FormControl>

            {/* Page Permissions */}
            <Box>
              <Typography variant="subtitle2" mb={1}>
                Page Permissions
              </Typography>
              <Grid container spacing={1}>
                {availablePages.map((page) => (
                  <Grid item xs={6} sm={4} key={page}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newUser.permissions[page] || false}
                          onChange={(e) =>
                            setNewUser((prev) => ({
                              ...prev,
                              permissions: {
                                ...prev.permissions,
                                [page]: e.target.checked,
                              },
                            }))
                          }
                        />
                      }
                      label={page.toUpperCase()}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddUserOpen(false)}
            sx={{
              border: "2px solid #ffe599",
              "&:hover": { bgcolor: "#ffe599" },
            }}
            color="black"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddUserSave}
            sx={{
              backgroundColor: "#ffe599",
              color: "#000",
            }}
          >
            Add User
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
